import { useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/enquiries`);
      setEnquiries(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="admin-page" className="min-h-screen bg-neutral-950 text-neutral-200 font-sans">
      <header className="border-b border-neutral-800 px-6 lg:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src="/logo-gold.png" alt="Delux Concierge" className="h-9 w-auto" />
          <span className="text-xs uppercase tracking-[0.3em] text-neutral-500">Enquiries Admin</span>
        </div>
        <button
          data-testid="admin-refresh-button"
          onClick={load}
          className="flex items-center gap-2 text-xs uppercase tracking-widest border border-neutral-700 px-4 py-2 hover:border-neutral-400 transition-colors"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </header>

      <main className="px-6 lg:px-12 py-10">
        <p className="text-sm text-neutral-500 mb-8" data-testid="admin-count">
          {enquiries.length} {enquiries.length === 1 ? "enquiry" : "enquiries"} received
        </p>

        {enquiries.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center py-32 text-neutral-600">
            <Inbox strokeWidth={1} size={48} />
            <p className="mt-6 text-sm tracking-wide">No enquiries yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-neutral-800">
            <table className="w-full text-sm" data-testid="admin-enquiries-table">
              <thead>
                <tr className="bg-neutral-900 text-left text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500">
                  <th className="px-5 py-4 font-medium">Received</th>
                  <th className="px-5 py-4 font-medium">Date of Enquiry</th>
                  <th className="px-5 py-4 font-medium">Name</th>
                  <th className="px-5 py-4 font-medium">Email</th>
                  <th className="px-5 py-4 font-medium">Phone</th>
                  <th className="px-5 py-4 font-medium">Country</th>
                  <th className="px-5 py-4 font-medium">Type</th>
                  <th className="px-5 py-4 font-medium">Additional Services</th>
                  <th className="px-5 py-4 font-medium">Message</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.map((e) => (
                  <tr key={e.id} data-testid={`admin-enquiry-row-${e.id}`} className="border-t border-neutral-800 hover:bg-neutral-900/60 transition-colors align-top">
                    <td className="px-5 py-4 whitespace-nowrap text-neutral-400">
                      {new Date(e.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-neutral-400" data-testid={`admin-enquiry-date-${e.id}`}>
                      {e.enquiry_date || "—"}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-neutral-100">{e.full_name || `${e.first_name || ""} ${e.last_name || ""}`.trim()}</td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <a href={`mailto:${e.email}`} className="text-amber-200/80 hover:underline">{e.email}</a>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-neutral-400">{e.phone || "—"}</td>
                    <td className="px-5 py-4 whitespace-nowrap text-neutral-400">{e.country || "—"}</td>
                    <td className="px-5 py-4 whitespace-nowrap text-neutral-300">{e.enquiry_type}</td>
                    <td className="px-5 py-4 text-neutral-400 max-w-xs" data-testid={`admin-additional-services-${e.id}`}>
                      {e.additional_services === "Yes" && e.additional_services_list?.length
                        ? e.additional_services_list.join(", ")
                        : "—"}
                    </td>
                    <td className="px-5 py-4 text-neutral-400 max-w-md">{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
